import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { WorkerStatusClient } from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { catchError } from 'rxjs/operators';
import { exhaustMap, map, Observable, of, startWith, Subject, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class WorkerStatusDictionaryService {
  private readonly workerStatusClient = inject(WorkerStatusClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public readonly totalCount: WritableSignal<number> = signal<number>(0);
  public readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public readonly hasLoaded: WritableSignal<boolean> = signal<boolean>(false);

  private readonly origin$: Subject<void> = new Subject<void>();

  public readonly source$ = this.origin$.pipe(
    startWith(void 0),
    tap((): void => {
      this.isLoading.set(true);
      this.hasLoaded.set(false);
    }),
    exhaustMap(() =>
      this.workerStatusClient.v1WorkerStatusGet().pipe(
        tap((items): void => {
          this.totalCount.set(items?.length ?? 0);
        }),
        map((items): IDictionaryItem<number>[] =>
          Array.isArray(items)
            ? items
                .filter((item): boolean => item.id !== undefined && item.id !== null)
                .map(
                  (item): IDictionaryItem<number> => ({
                    id: item.id as number,
                    name: this.translateService.instant(
                      'monitoring.workers.status.' + item.code.toLocaleLowerCase(),
                    ),
                  }),
                )
            : [],
        ),
        catchError((err: unknown): Observable<IDictionaryItem<number>[]> => {
          this.toastService.error({
            message: 'monitoring.selector.toast.worker-statuses-fetching-error',
          });

          console.error('Error fetching worker status dictionary items:', err);

          return of([]);
        }),
      ),
    ),
    tap((): void => {
      this.isLoading.set(false);
      this.hasLoaded.set(true);
    }),
  );
}
